/*
 * RichTextEditor — Editor de texto avanzado con modo dual (Visual / HTML).
 * Usa variables CSS del tema y componentes MDB.
 * Incluye sistema de selección, redimensionado y alineación de imágenes.
 * Ubicación: GlobalComponents/RichTextEditor/
 *
 * Props:
 *   initialHtml  {string}   — HTML inicial a cargar
 *   onChangeHtml {function} — Callback con el HTML completo al editar
 *   height       {number}   — Altura mínima del área editable (px, default 200)
 */

import React, { Component, createRef, Fragment } from "react";
import ReactDOM from "react-dom";
import {
  MDBBtn,
  MDBBtnGroup,
  MDBDropdown,
  MDBDropdownMenu,
  MDBDropdownToggle,
  MDBDropdownItem,
  MDBIcon,
} from "mdb-react-ui-kit";
import FileBase64Input from "./FileBase64Input";
import { MfmImageUploader } from "Projects/CC_cubi_clan_project/GlobalComponents/MfmImageUploader/MfmImageUploader";
import { mfmMediumUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";

import "./RichTextEditor.css";

export default class RichTextEditor extends Component {
  constructor(props) {
    super(props);
    this.divRef = createRef();
    this.htmlRef = createRef();
    this.wrapperRef = createRef();
    this.mfmUploaderRef = createRef();
    this.userEdited = false;

    // Image resize state
    this.resizing = false;
    this.resizeImg = null;
    this.resizeStartX = 0;
    this.resizeStartY = 0;
    this.resizeStartW = 0;
    this.resizeStartH = 0;
    this.resizeHandle = "";

    this.state = {
      mode: "visual",
      active: {
        bold: false, italic: false, underline: false, strike: false,
        ul: false, ol: false,
        justifyLeft: false, justifyCenter: false, justifyRight: false, justifyFull: false,
      },
      currentBlock: "P",
      showTablePopup: false,
      tableRows: 3,
      tableCols: 3,
      // Image selection
      selectedImg: null,
      imgToolbar: null,
      imgWidth: 0,
      imgHeight: 0,
      imgAlign: "none",
    };
  }

  /* ========== Lifecycle ========== */

  componentDidMount() {
    if (this.divRef.current) {
      this.divRef.current.innerHTML = this.props.initialHtml || "";
    }
    document.addEventListener("selectionchange", this.onSelectionChange, false);
    document.addEventListener("mousedown", this.onDocumentMouseDown, false);
    this.divRef.current?.addEventListener("keyup", this.updateToolbarState);
    this.divRef.current?.addEventListener("mouseup", this.updateToolbarState);
  }

  componentWillUnmount() {
    document.removeEventListener("selectionchange", this.onSelectionChange, false);
    document.removeEventListener("mousedown", this.onDocumentMouseDown, false);
    document.removeEventListener("mousemove", this.onResizeMove, false);
    document.removeEventListener("mouseup", this.onResizeEnd, false);
    this.divRef.current?.removeEventListener("keyup", this.updateToolbarState);
    this.divRef.current?.removeEventListener("mouseup", this.updateToolbarState);
  }

  componentDidUpdate(prevProps) {
    if (this.props.initialHtml !== prevProps.initialHtml && !this.userEdited) {
      if (this.divRef.current) {
        this.divRef.current.innerHTML = this.props.initialHtml || "";
        this.updateToolbarState();
      }
      if (this.htmlRef.current && this.state.mode === "html") {
        this.htmlRef.current.value = this.props.initialHtml || "";
      }
    }
  }

  /* ========== Mode switching ========== */

  switchMode = (newMode) => {
    if (newMode === this.state.mode) return;
    this.deselectImage();

    if (newMode === "html") {
      // Visual → HTML: copy innerHTML to textarea
      const html = this.divRef.current?.innerHTML || "";
      this.setState({ mode: "html" }, () => {
        if (this.htmlRef.current) this.htmlRef.current.value = html;
      });
    } else {
      // HTML → Visual: copy textarea content to contentEditable
      const html = this.htmlRef.current?.value || "";
      this.setState({ mode: "visual" }, () => {
        if (this.divRef.current) {
          this.divRef.current.innerHTML = html;
          this.emit();
          this.updateToolbarState();
        }
      });
    }
  };

  /* ========== ExecCommand helpers ========== */

  focusIfNeeded = () => {
    const sel = window.getSelection && window.getSelection();
    const within =
      sel && sel.rangeCount > 0
        ? this.divRef.current?.contains(sel.getRangeAt(0).commonAncestorContainer)
        : false;
    if (!within) {
      this.divRef.current?.focus();
      // Restore saved selection if available (e.g. after color picker steals focus)
      if (this._savedRange) {
        const s = window.getSelection();
        s.removeAllRanges();
        s.addRange(this._savedRange);
      }
    }
  };

  exec = (cmd, val = null) => {
    this.focusIfNeeded();
    document.execCommand(cmd, false, val);
    this.emit();
    this.updateToolbarState();
  };

  formatBlock = (block) => {
    this.focusIfNeeded();
    document.execCommand("formatBlock", false, block);
    this.emit();
    this.updateToolbarState();
  };

  /* ========== Inline features ========== */

  insertLink = () => {
    const url = prompt("URL del enlace:");
    if (!url) return;
    this.exec("createLink", url);
  };

  unlink = () => this.exec("unlink");

  insertImageFromUrl = () => {
    const url = prompt("URL de la imagen:");
    if (!url) return;
    this.focusIfNeeded();
    document.execCommand("insertHTML", false,
      `<img src="${url}" style="max-width:100%;height:auto" />`
    );
    this.emit();
  };

  insertFromMfm = (result) => {
    console.log('[RTE] insertFromMfm recibido:', result);
    if (!result?.uuid) { console.warn('[RTE] uuid nulo, abortando'); return; }
    this.focusIfNeeded();
    document.execCommand("insertHTML", false,
      `<img src="${mfmMediumUrl(result.uuid)}" data-uuid="${result.uuid}" style="max-width:100%;height:auto" />`
    );
    this.emit();
  };

  insertBase64 = (base64, dataUrl) => {
    const src = dataUrl || (base64 ? `data:image/jpeg;base64,${base64}` : "");
    if (!src) return;
    this.focusIfNeeded();
    document.execCommand("insertHTML", false,
      `<img src="${src}" style="max-width:100%;height:auto" />`
    );
    this.emit();
  };

  wrapSelection = (tag) => {
    this.focusIfNeeded();
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    try {
      const el = document.createElement(tag);
      range.surroundContents(el);
      this.emit();
      this.updateToolbarState();
    } catch {
      document.execCommand(
        "insertHTML",
        false,
        `<${tag}>${sel.toString()}</${tag}>`
      );
      this.emit();
      this.updateToolbarState();
    }
  };

  /* ========== Colors ========== */

  onForeColor = (e) => {
    this.exec("foreColor", e.target.value);
  };

  onHiliteColor = (e) => {
    this.exec("hiliteColor", e.target.value);
  };

  /* ========== Table insertion ========== */

  toggleTablePopup = () => {
    this.setState((prev) => ({ showTablePopup: !prev.showTablePopup }));
  };

  insertTable = () => {
    const { tableRows, tableCols } = this.state;
    const rows = Math.max(1, Math.min(tableRows, 20));
    const cols = Math.max(1, Math.min(tableCols, 20));

    let html = '<table style="border-collapse:collapse;width:100%;margin:1rem 0">';
    for (let r = 0; r < rows; r++) {
      html += "<tr>";
      for (let c = 0; c < cols; c++) {
        const tag = r === 0 ? "th" : "td";
        const style = 'style="border:1px solid #ccc;padding:0.5rem"';
        html += `<${tag} ${style}>&nbsp;</${tag}>`;
      }
      html += "</tr>";
    }
    html += "</table><p>&nbsp;</p>";

    this.focusIfNeeded();
    document.execCommand("insertHTML", false, html);
    this.emit();
    this.setState({ showTablePopup: false });
  };

  /* ========== Toolbar state ========== */

  getCurrentBlockName = () => {
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0) return "P";
    let node = sel.getRangeAt(0).commonAncestorContainer;
    if (node.nodeType === 3) node = node.parentNode;
    while (node && node !== this.divRef.current) {
      const tag = (node.tagName || "").toUpperCase();
      if (["H1", "H2", "H3", "H4", "H5", "H6", "P", "BLOCKQUOTE", "PRE"].includes(tag)) return tag;
      node = node.parentNode;
    }
    return "P";
  };

  updateToolbarState = () => {
    const q = (cmd) => {
      try { return document.queryCommandState(cmd); }
      catch { return false; }
    };
    const active = {
      bold: q("bold"),
      italic: q("italic"),
      underline: q("underline"),
      strike: q("strikeThrough"),
      ul: q("insertUnorderedList"),
      ol: q("insertOrderedList"),
      justifyLeft: q("justifyLeft"),
      justifyCenter: q("justifyCenter"),
      justifyRight: q("justifyRight"),
      justifyFull: q("justifyFull"),
    };
    const currentBlock = this.getCurrentBlockName();
    this.setState({ active, currentBlock });
  };

  onSelectionChange = () => {
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const within = this.divRef.current?.contains(sel.getRangeAt(0).commonAncestorContainer);
    if (within) {
      this._savedRange = sel.getRangeAt(0).cloneRange();
      this.updateToolbarState();
    }
  };

  /* ========== Emit ========== */

  emit = () => {
    const html = this.divRef.current?.innerHTML || "";
    this.userEdited = true;
    this.props.onChangeHtml?.(html);
  };

  onInput = () => {
    this.emit();
    this.updateToolbarState();
  };

  onHtmlTextareaChange = () => {
    const html = this.htmlRef.current?.value || "";
    this.userEdited = true;
    this.props.onChangeHtml?.(html);
  };

  /* ================================================================
     IMAGE SELECTION & RESIZE SYSTEM
     ================================================================ */

  onSurfaceClick = (e) => {
    const img = e.target.closest("img");
    if (img && this.divRef.current?.contains(img)) {
      e.preventDefault();
      e.stopPropagation();
      this.selectImage(img);
    } else {
      if (this.state.selectedImg) this.deselectImage();
    }
    this.onInput();
  };

  onDocumentMouseDown = (e) => {
    if (this.state.selectedImg && this.wrapperRef.current && !this.wrapperRef.current.contains(e.target)) {
      this.deselectImage();
    }
  };

  selectImage = (img) => {
    const w = img.offsetWidth || img.naturalWidth;
    const h = img.offsetHeight || img.naturalHeight;

    let align = "none";
    const style = (img.getAttribute("style") || "").toLowerCase();
    if (style.includes("float:") || style.includes("float :")) {
      if (style.includes("float: left") || style.includes("float:left")) align = "left";
      else if (style.includes("float: right") || style.includes("float:right")) align = "right";
    }
    if (style.includes("display: block") || style.includes("display:block")) {
      if (style.includes("margin: 0 auto") || style.includes("margin:0 auto") ||
          style.includes("margin: 0px auto") || style.includes("margin:0px auto") ||
          style.includes("margin-left: auto") || style.includes("margin-right: auto")) {
        align = "center";
      }
    }

    this.clearAllImgSelections();
    img.classList.add("rte-img-selected");

    const surfaceRect = this.divRef.current.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    this.setState({
      selectedImg: img,
      imgWidth: w,
      imgHeight: h,
      imgAlign: align,
      imgToolbar: {
        top: imgRect.top - surfaceRect.top - 40,
        left: imgRect.left - surfaceRect.left,
      },
    });
  };

  deselectImage = () => {
    this.clearAllImgSelections();
    this.setState({ selectedImg: null, imgToolbar: null });
  };

  clearAllImgSelections = () => {
    this.divRef.current?.querySelectorAll(".rte-img-selected").forEach((img) => {
      img.classList.remove("rte-img-selected");
    });
  };

  setImageWidth = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!val || val < 10 || !this.state.selectedImg) return;
    const img = this.state.selectedImg;
    const ratio = img.naturalHeight / img.naturalWidth;
    const newH = Math.round(val * ratio);
    img.style.width = val + "px";
    img.style.height = newH + "px";
    this.setState({ imgWidth: val, imgHeight: newH });
    this.emit();
  };

  setImageHeight = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!val || val < 10 || !this.state.selectedImg) return;
    const img = this.state.selectedImg;
    const ratio = img.naturalWidth / img.naturalHeight;
    const newW = Math.round(val * ratio);
    img.style.height = val + "px";
    img.style.width = newW + "px";
    this.setState({ imgHeight: val, imgWidth: newW });
    this.emit();
  };

  resetImageSize = () => {
    const img = this.state.selectedImg;
    if (!img) return;
    img.style.width = "";
    img.style.height = "";
    img.style.maxWidth = "100%";
    this.setState({
      imgWidth: img.naturalWidth,
      imgHeight: img.naturalHeight,
    });
    this.emit();
  };

  setImageAlign = (align) => {
    const img = this.state.selectedImg;
    if (!img) return;

    img.style.float = "";
    img.style.display = "";
    img.style.marginLeft = "";
    img.style.marginRight = "";

    switch (align) {
      case "left":
        img.style.float = "left";
        img.style.marginRight = "1rem";
        img.style.marginBottom = "0.5rem";
        break;
      case "center":
        img.style.display = "block";
        img.style.marginLeft = "auto";
        img.style.marginRight = "auto";
        break;
      case "right":
        img.style.float = "right";
        img.style.marginLeft = "1rem";
        img.style.marginBottom = "0.5rem";
        break;
      default:
        break;
    }

    this.setState({ imgAlign: align });
    this.emit();
  };

  setImageAlt = () => {
    const img = this.state.selectedImg;
    if (!img) return;
    const current = img.getAttribute("alt") || "";
    const alt = prompt("Texto alternativo (alt):", current);
    if (alt !== null) {
      img.setAttribute("alt", alt);
      this.emit();
    }
  };

  deleteImage = () => {
    const img = this.state.selectedImg;
    if (!img) return;
    img.remove();
    this.deselectImage();
    this.emit();
  };

  /* ---- Drag resize handles ---- */

  onResizeStart = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    const img = this.state.selectedImg;
    if (!img) return;

    this.resizing = true;
    this.resizeImg = img;
    this.resizeHandle = handle;
    this.resizeStartX = e.clientX;
    this.resizeStartY = e.clientY;
    this.resizeStartW = img.offsetWidth;
    this.resizeStartH = img.offsetHeight;

    document.addEventListener("mousemove", this.onResizeMove, false);
    document.addEventListener("mouseup", this.onResizeEnd, false);
  };

  onResizeMove = (e) => {
    if (!this.resizing || !this.resizeImg) return;
    e.preventDefault();

    const dx = e.clientX - this.resizeStartX;
    const dy = e.clientY - this.resizeStartY;
    const ratio = this.resizeStartH / this.resizeStartW;
    const handle = this.resizeHandle;

    let newW = this.resizeStartW;
    let newH = this.resizeStartH;

    if (handle.includes("e")) {
      newW = Math.max(30, this.resizeStartW + dx);
      newH = Math.round(newW * ratio);
    } else if (handle.includes("w")) {
      newW = Math.max(30, this.resizeStartW - dx);
      newH = Math.round(newW * ratio);
    }

    if (handle === "s" || handle === "n") {
      newH = handle === "s"
        ? Math.max(30, this.resizeStartH + dy)
        : Math.max(30, this.resizeStartH - dy);
      newW = Math.round(newH / ratio);
    }

    this.resizeImg.style.width = newW + "px";
    this.resizeImg.style.height = newH + "px";
    this.setState({ imgWidth: newW, imgHeight: newH });
  };

  onResizeEnd = () => {
    if (this.resizing) {
      this.resizing = false;
      this.emit();
      if (this.resizeImg) this.selectImage(this.resizeImg);
    }
    document.removeEventListener("mousemove", this.onResizeMove, false);
    document.removeEventListener("mouseup", this.onResizeEnd, false);
  };

  /* ========== Render helpers ========== */

  renderImageHandles() {
    const { selectedImg } = this.state;
    if (!selectedImg || this.state.mode !== "visual") return null;

    const surfaceRect = this.divRef.current?.getBoundingClientRect();
    const imgRect = selectedImg.getBoundingClientRect();
    if (!surfaceRect || !imgRect) return null;

    const top = imgRect.top - surfaceRect.top;
    const left = imgRect.left - surfaceRect.left;
    const w = imgRect.width;
    const h = imgRect.height;

    const handles = ["nw", "ne", "se", "sw", "n", "e", "s", "w"];
    const positions = {
      nw: { top: -4, left: -4 },
      ne: { top: -4, left: w - 4 },
      se: { top: h - 4, left: w - 4 },
      sw: { top: h - 4, left: -4 },
      n:  { top: -4, left: w / 2 - 4 },
      s:  { top: h - 4, left: w / 2 - 4 },
      e:  { top: h / 2 - 4, left: w - 4 },
      w:  { top: h / 2 - 4, left: -4 },
    };

    return (
      <div
        className="rte-handles-frame"
        style={{ top, left, width: w, height: h }}
      >
        {handles.map((dir) => (
          <div
            key={dir}
            className={`rte-handle rte-handle--${dir}`}
            style={{ top: positions[dir].top, left: positions[dir].left }}
            onMouseDown={(e) => this.onResizeStart(e, dir)}
          />
        ))}
      </div>
    );
  }

  renderImageToolbar() {
    const { selectedImg, imgToolbar, imgWidth, imgHeight, imgAlign } = this.state;
    if (!selectedImg || !imgToolbar || this.state.mode !== "visual") return null;

    return (
      <div className="rte-img-toolbar" style={{ top: imgToolbar.top, left: imgToolbar.left }}>
        {/* Dimensions */}
        <div className="rte-img-dims">
          <label>W</label>
          <input type="number" value={imgWidth || ""} min="30" onChange={this.setImageWidth} />
          <label>H</label>
          <input type="number" value={imgHeight || ""} min="30" onChange={this.setImageHeight} />
          <button type="button" title="Tamaño original" onClick={this.resetImageSize}>
            <MDBIcon icon="undo-alt" />
          </button>
        </div>

        <span className="rte-img-sep" />

        {/* Alignment */}
        <MDBBtnGroup size="sm">
          <MDBBtn color="secondary" outline active={imgAlign === "none"} onClick={() => this.setImageAlign("none")} title="En línea">
            <MDBIcon icon="minus" />
          </MDBBtn>
          <MDBBtn color="secondary" outline active={imgAlign === "left"} onClick={() => this.setImageAlign("left")} title="Flotar izquierda">
            <MDBIcon icon="align-left" />
          </MDBBtn>
          <MDBBtn color="secondary" outline active={imgAlign === "center"} onClick={() => this.setImageAlign("center")} title="Centrar">
            <MDBIcon icon="align-center" />
          </MDBBtn>
          <MDBBtn color="secondary" outline active={imgAlign === "right"} onClick={() => this.setImageAlign("right")} title="Flotar derecha">
            <MDBIcon icon="align-right" />
          </MDBBtn>
        </MDBBtnGroup>

        <span className="rte-img-sep" />

        {/* Alt text & Delete */}
        <button type="button" className="rte-img-action" onClick={this.setImageAlt} title="Texto alt">
          <MDBIcon icon="font" />
        </button>
        <button type="button" className="rte-img-action rte-img-action--danger" onClick={this.deleteImage} title="Eliminar imagen">
          <MDBIcon icon="trash" />
        </button>
      </div>
    );
  }

  /* ========== Render ========== */

  render() {
    const { active, currentBlock, mode, showTablePopup, tableRows, tableCols } = this.state;
    const height = this.props.height || 200;

    return (
      <Fragment>
        <div className="rte" ref={this.wrapperRef}>
        {/* Mode tabs */}
        <div className="rte-mode-tabs">
          <button
            type="button"
            className={`rte-mode-tab ${mode === "visual" ? "rte-mode-tab--active" : ""}`}
            onClick={() => this.switchMode("visual")}
          >
            <MDBIcon icon="eye" className="me-1" />
            Visual
          </button>
          <button
            type="button"
            className={`rte-mode-tab ${mode === "html" ? "rte-mode-tab--active" : ""}`}
            onClick={() => this.switchMode("html")}
          >
            <MDBIcon icon="code" className="me-1" />
            HTML
          </button>
        </div>

        {/* Toolbar — only in visual mode */}
        {mode === "visual" && (
          <div className="rte-toolbar">
            {/* Block format */}
            <MDBDropdown className="rte-toolbar-group">
              <MDBDropdownToggle size="sm" color="secondary" outline>
                {currentBlock === "P" ? "Párrafo" : currentBlock}
              </MDBDropdownToggle>
              <MDBDropdownMenu>
                <MDBDropdownItem link onClick={() => this.formatBlock("P")}>Párrafo</MDBDropdownItem>
                <MDBDropdownItem link onClick={() => this.formatBlock("H1")}>H1</MDBDropdownItem>
                <MDBDropdownItem link onClick={() => this.formatBlock("H2")}>H2</MDBDropdownItem>
                <MDBDropdownItem link onClick={() => this.formatBlock("H3")}>H3</MDBDropdownItem>
                <MDBDropdownItem link onClick={() => this.formatBlock("H4")}>H4</MDBDropdownItem>
                <MDBDropdownItem link onClick={() => this.formatBlock("BLOCKQUOTE")}>Cita</MDBDropdownItem>
                <MDBDropdownItem link onClick={() => this.formatBlock("PRE")}>Pre</MDBDropdownItem>
              </MDBDropdownMenu>
            </MDBDropdown>

            <span className="rte-toolbar-sep" />

            {/* Inline formatting */}
            <MDBBtn size="sm" color="secondary" outline active={active.bold} onClick={() => this.exec("bold")} title="Negrita">
              <strong>B</strong>
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline active={active.italic} onClick={() => this.exec("italic")} title="Cursiva">
              <em>i</em>
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline active={active.underline} onClick={() => this.exec("underline")} title="Subrayado">
              <u>U</u>
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline active={active.strike} onClick={() => this.exec("strikeThrough")} title="Tachado">
              <s>S</s>
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline onClick={() => this.exec("superscript")} title="Superíndice">
              x<sup>2</sup>
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline onClick={() => this.exec("subscript")} title="Subíndice">
              x<sub>2</sub>
            </MDBBtn>

            <span className="rte-toolbar-sep" />

            {/* Colors */}
            <label className="rte-color-btn" title="Color de texto">
              <MDBIcon icon="font" />
              <input type="color" defaultValue="#000000" onChange={this.onForeColor} />
            </label>
            <label className="rte-color-btn" title="Color de fondo">
              <MDBIcon icon="highlighter" />
              <input type="color" defaultValue="#ffff00" onChange={this.onHiliteColor} />
            </label>

            <span className="rte-toolbar-sep" />

            {/* Lists */}
            <MDBBtn size="sm" color="secondary" outline active={active.ul} onClick={() => this.exec("insertUnorderedList")} title="Lista sin orden">
              <MDBIcon icon="list-ul" />
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline active={active.ol} onClick={() => this.exec("insertOrderedList")} title="Lista ordenada">
              <MDBIcon icon="list-ol" />
            </MDBBtn>

            {/* Indent */}
            <MDBBtn size="sm" color="secondary" outline onClick={() => this.exec("indent")} title="Aumentar sangría">
              <MDBIcon icon="indent" />
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline onClick={() => this.exec("outdent")} title="Reducir sangría">
              <MDBIcon icon="outdent" />
            </MDBBtn>

            <span className="rte-toolbar-sep" />

            {/* Alignment */}
            <MDBBtn size="sm" color="secondary" outline active={active.justifyLeft} onClick={() => this.exec("justifyLeft")} title="Alinear izquierda">
              <MDBIcon icon="align-left" />
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline active={active.justifyCenter} onClick={() => this.exec("justifyCenter")} title="Centrar">
              <MDBIcon icon="align-center" />
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline active={active.justifyRight} onClick={() => this.exec("justifyRight")} title="Alinear derecha">
              <MDBIcon icon="align-right" />
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline active={active.justifyFull} onClick={() => this.exec("justifyFull")} title="Justificar">
              <MDBIcon icon="align-justify" />
            </MDBBtn>

            <span className="rte-toolbar-sep" />

            {/* Links */}
            <MDBBtn size="sm" color="secondary" outline onClick={this.insertLink} title="Insertar enlace">
              <MDBIcon icon="link" />
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline onClick={this.unlink} title="Quitar enlace">
              <MDBIcon icon="unlink" />
            </MDBBtn>

            {/* Code inline + HR */}
            <MDBBtn size="sm" color="secondary" outline onClick={() => this.wrapSelection("code")} title="Código inline">
              {"</>"}
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline onClick={() => this.exec("insertHorizontalRule")} title="Separador horizontal">
              <MDBIcon icon="minus" />
            </MDBBtn>

            <span className="rte-toolbar-sep" />

            {/* Table */}
            <div className="rte-table-wrapper">
              <MDBBtn size="sm" color="secondary" outline onClick={this.toggleTablePopup} title="Insertar tabla">
                <MDBIcon icon="table" />
              </MDBBtn>
              {showTablePopup && (
                <div className="rte-table-popup">
                  <div className="rte-table-popup-row">
                    <label>Filas:</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={tableRows}
                      onChange={(e) => this.setState({ tableRows: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div className="rte-table-popup-row">
                    <label>Columnas:</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={tableCols}
                      onChange={(e) => this.setState({ tableCols: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <MDBBtn size="sm" color="primary" onClick={this.insertTable}>
                    Insertar
                  </MDBBtn>
                </div>
              )}
            </div>

            {/* Images */}
            <MDBBtn size="sm" color="secondary" outline onClick={this.insertImageFromUrl} title="Imagen desde URL">
              <MDBIcon far icon="image" />
            </MDBBtn>
            {this.props.appCode ? (
              <MDBBtn size="sm" color="secondary" outline onClick={() => {
                console.log('[RTE] Abriendo MFM picker, appCode:', this.props.appCode);
                this.mfmUploaderRef.current?.openModal();
              }} title="Subir / seleccionar imagen desde MFM">
                <MDBIcon icon="upload" />
              </MDBBtn>
            ) : (
              <FileBase64Input label={<MDBIcon icon="upload" />} onBase64={this.insertBase64} />
            )}

            <span className="rte-toolbar-sep" />

            {/* Undo / Redo / Clear */}
            <MDBBtn size="sm" color="secondary" outline onClick={() => this.exec("undo")} title="Deshacer">
              <MDBIcon icon="undo" />
            </MDBBtn>
            <MDBBtn size="sm" color="secondary" outline onClick={() => this.exec("redo")} title="Rehacer">
              <MDBIcon icon="redo" />
            </MDBBtn>
            <MDBBtn size="sm" color="danger" outline onClick={() => this.exec("removeFormat")} title="Limpiar formato">
              <MDBIcon icon="eraser" />
            </MDBBtn>
          </div>
        )}

        {/* Visual surface with image overlay system */}
        <div className="rte-surface-wrapper" style={{ display: mode === "visual" ? "block" : "none" }}>
          <div
            ref={this.divRef}
            className="rte-surface"
            contentEditable
            suppressContentEditableWarning
            onInput={this.onInput}
            onClick={this.onSurfaceClick}
            style={{ minHeight: height }}
          />
          {this.renderImageHandles()}
          {this.renderImageToolbar()}
        </div>

        {/* HTML source textarea */}
        {mode === "html" && (
          <textarea
            ref={this.htmlRef}
            className="rte-html-source"
            style={{ minHeight: height }}
            onChange={this.onHtmlTextareaChange}
            spellCheck={false}
          />
        )}
      </div>

      {this.props.appCode && ReactDOM.createPortal(
        <MfmImageUploader
          ref={this.mfmUploaderRef}
          headless
          appCode={this.props.appCode}
          library={this.props.mfmLibrary}
          mediaType="image"
          onUpload={this.insertFromMfm}
        />,
        document.body
      )}
    </Fragment>
    );
  }
}
